import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useReactToPrint } from 'react-to-print';
import axios from 'axios';
import SherDineNav from './SherDineNav';
import SideNav from './SideNav';
import TopRootDiv from './TopRootDiv';
import { DBContext } from './DBContext';
import { exportToCSV } from '../utils/exportUtils';

// ─── helpers ────────────────────────────────────────────────────────────────
const fmt   = (v) => `₹${parseFloat(v || 0).toFixed(2)}`;
const todayISO = () => new Date().toISOString().slice(0, 10);

const GST_COLS = [
    { key: 'BillNo',      label: 'Bill No'      },
    { key: 'billDate',    label: 'Date'          },
    { key: 'dinetype',    label: 'Type'          },
    { key: 'grossAmount', label: 'Gross (₹)'    },
    { key: 'discount',    label: 'Discount (₹)' },
    { key: 'netAmount',   label: 'Net (₹)'      },
    { key: 'cgst',        label: 'CGST (₹)'     },
    { key: 'sgst',        label: 'SGST (₹)'     },
    { key: 'cess',        label: 'Cess (₹)'     },
    { key: 'totalTax',    label: 'Total Tax (₹)'},
];

// ─── PrintView ───────────────────────────────────────────────────────────────
const PrintView = React.forwardRef(({ data, dates, summary }, ref) => (
    <div ref={ref} style={{ padding: '20px', fontFamily: 'Arial, sans-serif', fontSize: '11px' }}>
        <h2 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '4px' }}>GST / Tax Report</h2>
        <p style={{ color: '#666', marginBottom: '14px' }}>
            {dates.sdate && dates.edate ? `${dates.sdate}  →  ${dates.edate}` : 'Today'}
        </p>

        {/* Summary */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
            {summary.map(({ label, value }) => (
                <div key={label} style={{ background: '#f5f5f5', borderRadius: '6px', padding: '6px 14px' }}>
                    <div style={{ fontSize: '9px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#0008B4' }}>{value}</div>
                </div>
            ))}
        </div>

        {/* Table */}
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
                <tr style={{ background: '#0008B4', color: '#fff' }}>
                    {GST_COLS.map(c => (
                        <th key={c.key} style={{ padding: '6px 8px', textAlign: 'left', fontSize: '10px' }}>{c.label}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {data.map((row, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                        {GST_COLS.map(c => (
                            <td key={c.key} style={{ padding: '5px 8px', borderBottom: '1px solid #eee', fontSize: '10px' }}>
                                {['grossAmount','discount','netAmount','cgst','sgst','cess','totalTax'].includes(c.key)
                                    ? fmt(row[c.key]) : (row[c.key] ?? '')}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
));

// ─── Main Component ──────────────────────────────────────────────────────────
function GSTReport() {
    const { logginedDB } = useContext(DBContext);
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

    const [sideNav, setSideNav] = useState(false);
    const [falseState, setFalse] = useState(null);
    const [dates, setDates]     = useState({ sdate: todayISO(), edate: todayISO() });
    const [data, setData]       = useState([]);
    const [loading, setLoading] = useState(false);

    const printRef = useRef(null);

    // ── fetch ──
    const fetchData = useCallback(async () => {
        if (!logginedDB) return;
        setLoading(true);
        try {
            const res = await axios.get(
                `${apiBaseUrl}/GSTReport/${logginedDB}?sdate=${dates.sdate}&edate=${dates.edate}`
            );
            setData(res.data || []);
        } catch (e) {
            console.error('GSTReport fetch error:', e);
        } finally {
            setLoading(false);
        }
    }, [logginedDB, dates, apiBaseUrl]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // ── print handler ──
    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        documentTitle: `GST_Report_${dates.sdate}_${dates.edate}`,
    });

    // ── aggregated summary ──
    const totalBills   = data.length;
    const totalGross   = data.reduce((s, r) => s + parseFloat(r.grossAmount || 0), 0);
    const totalNet     = data.reduce((s, r) => s + parseFloat(r.netAmount   || 0), 0);
    const totalCGST    = data.reduce((s, r) => s + parseFloat(r.cgst        || 0), 0);
    const totalSGST    = data.reduce((s, r) => s + parseFloat(r.sgst        || 0), 0);
    const totalCess    = data.reduce((s, r) => s + parseFloat(r.cess        || 0), 0);
    const totalTax     = data.reduce((s, r) => s + parseFloat(r.totalTax    || 0), 0);

    const summaryCards = [
        { label: 'Bills',       value: totalBills          },
        { label: 'Gross',       value: fmt(totalGross)     },
        { label: 'Net Revenue', value: fmt(totalNet)       },
        { label: 'CGST',        value: fmt(totalCGST)      },
        { label: 'SGST',        value: fmt(totalSGST)      },
        { label: 'Cess',        value: fmt(totalCess)      },
        { label: 'Total Tax',   value: fmt(totalTax)       },
    ];

    return (
        <div className='w-screen h-screen hidden sm:flex sm:flex-col overflow-hidden'>
            <SherDineNav sideNav={(s) => setSideNav(s)} falseState={falseState} />
            <div className='w-full bg-[#f5f5f5] flex-grow flex gap-3 overflow-hidden'>
                <div className='w-max h-full z-[9999]'>
                    <SideNav focus={'Report'} state={sideNav} falseState={(s) => setFalse(s)} />
                </div>
                <div className='w-full h-full flex flex-col pr-3 pl-0 pb-3 pt-0 gap-3 overflow-hidden'>
                    {/* Header */}
                    <div className='pr-3 h-max w-full bg-[#fff] borderBottom'>
                        <TopRootDiv image={'reports'} root={['Report', 'GST / Tax Report']} />
                    </div>

                    <div className='w-full h-full overflow-auto p-3 flex flex-col gap-3 font-nunito'>

                        {/* Controls bar */}
                        <div className='w-full flex flex-wrap gap-2 items-end bg-white rounded-xl p-3 shadow-sm border border-[#EBEBEB]'>
                            <div className='flex flex-col gap-1'>
                                <label className='text-[.7rem] text-[#888] font-semibold uppercase tracking-wide'>From</label>
                                <input type='date' value={dates.sdate}
                                    onChange={e => setDates(d => ({ ...d, sdate: e.target.value }))}
                                    className='border border-[#D5D5D5] rounded-lg px-3 py-1.5 text-[.85rem] focus:outline-none focus:ring-2 focus:ring-[#0008B4]/30' />
                            </div>
                            <div className='flex flex-col gap-1'>
                                <label className='text-[.7rem] text-[#888] font-semibold uppercase tracking-wide'>To</label>
                                <input type='date' value={dates.edate}
                                    onChange={e => setDates(d => ({ ...d, edate: e.target.value }))}
                                    className='border border-[#D5D5D5] rounded-lg px-3 py-1.5 text-[.85rem] focus:outline-none focus:ring-2 focus:ring-[#0008B4]/30' />
                            </div>
                            <button onClick={fetchData}
                                className='px-5 py-1.5 bg-[#0008B4] text-white rounded-lg text-[.85rem] font-semibold hover:bg-[#0006a0] transition-colors'>
                                Search
                            </button>
                            <div className='flex-1' />
                            <button onClick={() => exportToCSV(`GST_Report_${dates.sdate}_${dates.edate}`, GST_COLS, data)}
                                className='px-4 py-1.5 border border-[#D5D5D5] rounded-lg text-[.8rem] font-semibold text-[#464255] hover:bg-[#f5f5f5] transition-colors'>
                                ↓ CSV
                            </button>
                            <button onClick={handlePrint}
                                className='px-4 py-1.5 border border-[#D5D5D5] rounded-lg text-[.8rem] font-semibold text-[#464255] hover:bg-[#f5f5f5] transition-colors'>
                                🖨 Print / PDF
                            </button>
                        </div>

                        {/* Summary cards */}
                        <div className='grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3'>
                            {summaryCards.map(({ label, value }) => (
                                <div key={label} className='bg-white rounded-xl p-4 shadow-sm border border-[#EBEBEB] flex flex-col gap-1'>
                                    <span className='text-[.7rem] text-[#888] font-semibold uppercase tracking-wide'>{label}</span>
                                    <span className='text-[1.1rem] font-bold text-[#0008B4]'>{value}</span>
                                </div>
                            ))}
                        </div>

                        {/* Tax breakdown progress bars (visual) */}
                        {totalTax > 0 && (
                            <div className='bg-white rounded-xl p-4 shadow-sm border border-[#EBEBEB]'>
                                <span className='text-[.82rem] font-semibold text-[#464255]'>Tax Composition</span>
                                <div className='mt-3 flex flex-col gap-2 text-[.8rem]'>
                                    {[
                                        { label: 'CGST',  val: totalCGST, color: '#0008B4' },
                                        { label: 'SGST',  val: totalSGST, color: '#57BEB5' },
                                        { label: 'Cess',  val: totalCess, color: '#AE59DC' },
                                    ].map(({ label, val, color }) => (
                                        <div key={label} className='flex items-center gap-3'>
                                            <span className='w-10 text-[#888] text-right'>{label}</span>
                                            <div className='flex-1 h-2.5 bg-[#f0f0f0] rounded-full'>
                                                <div className='h-full rounded-full transition-all'
                                                    style={{ width: `${totalTax > 0 ? (val / totalTax * 100).toFixed(1) : 0}%`, background: color }} />
                                            </div>
                                            <span className='w-28 text-right font-semibold' style={{ color }}>{fmt(val)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Table */}
                        <div className='bg-white rounded-xl shadow-sm border border-[#EBEBEB] overflow-hidden flex-1 min-h-0'>
                            {loading ? (
                                <div className='w-full h-48 flex items-center justify-center text-[#888] text-[.9rem]'>Loading…</div>
                            ) : data.length === 0 ? (
                                <div className='w-full h-48 flex items-center justify-center text-[#888] text-[.9rem]'>No data for selected range</div>
                            ) : (
                                <div className='overflow-auto h-full'>
                                    <table className='w-full text-[.82rem]'>
                                        <thead className='sticky top-0'>
                                            <tr className='bg-[#0008B4] text-white'>
                                                <th className='text-left px-3 py-2.5 font-semibold'>Bill No</th>
                                                <th className='text-left px-3 py-2.5 font-semibold'>Date</th>
                                                <th className='text-left px-3 py-2.5 font-semibold'>Type</th>
                                                <th className='text-right px-3 py-2.5 font-semibold'>Gross</th>
                                                <th className='text-right px-3 py-2.5 font-semibold'>Discount</th>
                                                <th className='text-right px-3 py-2.5 font-semibold'>Net</th>
                                                <th className='text-right px-3 py-2.5 font-semibold'>CGST</th>
                                                <th className='text-right px-3 py-2.5 font-semibold'>SGST</th>
                                                <th className='text-right px-3 py-2.5 font-semibold'>Cess</th>
                                                <th className='text-right px-3 py-2.5 font-semibold bg-[#0006a0]'>Total Tax</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.map((row, i) => (
                                                <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#f9f9f9]'}>
                                                    <td className='px-3 py-2 font-medium text-[#464255]'>{row.BillNo}</td>
                                                    <td className='px-3 py-2 text-[#464255]'>{row.billDate}</td>
                                                    <td className='px-3 py-2 text-[#888]'>{row.dinetype || '—'}</td>
                                                    <td className='px-3 py-2 text-right text-[#464255]'>{fmt(row.grossAmount)}</td>
                                                    <td className='px-3 py-2 text-right text-[#c30000]'>{parseFloat(row.discount) > 0 ? `−${fmt(row.discount)}` : '—'}</td>
                                                    <td className='px-3 py-2 text-right font-semibold text-[#0008B4]'>{fmt(row.netAmount)}</td>
                                                    <td className='px-3 py-2 text-right text-[#57BEB5]'>{parseFloat(row.cgst) > 0 ? fmt(row.cgst) : '—'}</td>
                                                    <td className='px-3 py-2 text-right text-[#57BEB5]'>{parseFloat(row.sgst) > 0 ? fmt(row.sgst) : '—'}</td>
                                                    <td className='px-3 py-2 text-right text-[#AE59DC]'>{parseFloat(row.cess) > 0 ? fmt(row.cess) : '—'}</td>
                                                    <td className='px-3 py-2 text-right font-bold text-[#0008B4] bg-[#EEF0FF]'>{fmt(row.totalTax)}</td>
                                                </tr>
                                            ))}
                                            {/* Totals row */}
                                            <tr className='bg-[#EEF0FF] font-bold text-[.82rem] sticky bottom-0'>
                                                <td colSpan={3} className='px-3 py-2 text-[#0008B4]'>TOTAL ({totalBills} bills)</td>
                                                <td className='px-3 py-2 text-right text-[#464255]'>{fmt(totalGross)}</td>
                                                <td className='px-3 py-2 text-right text-[#c30000]'></td>
                                                <td className='px-3 py-2 text-right text-[#0008B4]'>{fmt(totalNet)}</td>
                                                <td className='px-3 py-2 text-right text-[#57BEB5]'>{fmt(totalCGST)}</td>
                                                <td className='px-3 py-2 text-right text-[#57BEB5]'>{fmt(totalSGST)}</td>
                                                <td className='px-3 py-2 text-right text-[#AE59DC]'>{fmt(totalCess)}</td>
                                                <td className='px-3 py-2 text-right text-[#0008B4] bg-[#dde0f8]'>{fmt(totalTax)}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Hidden print target */}
            <div className='hidden'>
                <PrintView ref={printRef} data={data} dates={dates} summary={summaryCards} />
            </div>
        </div>
    );
}

export default GSTReport;
